// Blob externalization for a turn payload.
//
// A persisted turn payload is the item subtree the chat UI renders — including
// tool-card `details` (frozen tables/charts, byte-clipped to ~512 KiB), pasted
// images, and oversized tool args. `stripTransientFromItems` (chatSerializer)
// deliberately does NOT strip `details`, so a naive sync would ship full row
// sets inline and blow past the sync route's per-turn payload cap. This pass
// walks the payload, lifts every string leaf over the threshold into an
// out-of-band blob, and replaces it with a compact placeholder — keeping the
// inline payload small so the assembled turn stays well under that cap.
//
// Pure and two-phase so the network step (mint → PUT → finalize) sits in the
// engine, not here:
//   1. extractOversizedLeaves(payload) → { payload (with placeholders), blobs }
//      Each blob carries a synthetic local `ref`; the placeholder is
//      { [BLOB_MARKER]: ref }.
//   2. applyBlobIds(payload, refToBlobId) → payload with each placeholder's
//      ref swapped for the server-minted blob id, plus a flat blob_refs array
//      ready for the sync body.
//
// On restore, hydrateBlobPlaceholders walks the same markers and asks a
// caller-supplied loader to fetch each blob lazily (card-expand time).

// Marker key on a placeholder object. Chosen to not collide with any real
// item/detail field, and stable so restore can find it.
export const BLOB_MARKER = "$chatBlob";

// Externalize any string leaf whose UTF-8 size exceeds this. Well under the
// backend's 4 MiB per-turn payload cap (CHAT_TURN_PAYLOAD_LIMIT_BYTES) so a
// turn carrying several placeholders stays clear of it, and near the
// tool-result store's small-entry notion so what the model treated as "large"
// is what gets externalized.
export const DEFAULT_LEAF_THRESHOLD_BYTES = 256 * 1024;

const _encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;

// UTF-8 byte length of a string. Falls back to char length where TextEncoder
// is absent (non-browser test shims) — an under-estimate, but the threshold is
// soft and the backend re-checks.
export function utf8Bytes(s) {
    if (typeof s !== "string") return 0;
    if (_encoder) return _encoder.encode(s).length;
    return s.length;
}

// SHA-256 hex of a leaf's content — the key the sync engine's per-chat dedup
// cache is built on, and the reason an unchanged leaf isn't re-uploaded every
// boundary. Lives here rather than in the engine because both the upload side
// and the restore side (which seeds that cache from what it just downloaded)
// must derive the SAME key from the SAME content. Returns null where WebCrypto
// is unavailable (non-browser test shims), which simply disables dedup (every
// leaf re-uploads), never mis-reuses.
export async function sha256Hex(str) {
    if (typeof str !== "string") return null;
    if (typeof crypto === "undefined" || !crypto?.subtle || typeof TextEncoder === "undefined") {
        return null;
    }
    try {
        const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
        return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
    } catch {
        return null;
    }
}

// Canonical sentinel for a blob-bearing leaf, used only for comparison. A NUL
// wrapper can't collide with real content.
const BLOB_SENTINEL = "\u0000$chatBlob\u0000";

// Project a payload into a blob-CONTENT-agnostic shape: every externalized-blob
// placeholder AND every still-inline oversized string leaf collapses to one
// sentinel. This lets a locally-inline leaf compare equal to its server-side
// placeholder (the same content in two storage forms) — the conflict resolver
// needs that, since the local IDB body stores leaves inline while the server
// copy stores them as placeholders, so a raw hash would spuriously diverge on
// every heavy turn. Pure; input untouched.
export function blobAgnosticLeaves(value, thresholdBytes = DEFAULT_LEAF_THRESHOLD_BYTES) {
    if (Array.isArray(value)) return value.map((v) => blobAgnosticLeaves(v, thresholdBytes));
    if (value && typeof value === "object") {
        if (Object.prototype.hasOwnProperty.call(value, BLOB_MARKER)) return BLOB_SENTINEL;
        const out = {};
        for (const [k, v] of Object.entries(value)) out[k] = blobAgnosticLeaves(v, thresholdBytes);
        return out;
    }
    if (typeof value === "string" && utf8Bytes(value) > thresholdBytes) return BLOB_SENTINEL;
    return value;
}

// Split a large string leaf into its blob upload shape. A `data:<mime>;base64,`
// URL (pasted images) uploads the decoded bytes under its real content type so
// restore can serve it back verbatim; any other string uploads as UTF-8 text.
function describeLeaf(value) {
    const dataUrl = /^data:([^;,]+);base64,(.*)$/s.exec(value);
    if (dataUrl) {
        return { contentType: dataUrl[1] || "application/octet-stream", encoding: "data-url", data: value };
    }
    return { contentType: "text/plain; charset=utf-8", encoding: "utf8", data: value };
}

// Walk `payload`, replacing each oversized string leaf with a placeholder and
// collecting the blobs to upload. Returns a NEW payload (input untouched).
export function extractOversizedLeaves(payload, { thresholdBytes = DEFAULT_LEAF_THRESHOLD_BYTES } = {}) {
    const blobs = [];
    let counter = 0;
    const walk = (value) => {
        if (typeof value === "string") {
            if (utf8Bytes(value) <= thresholdBytes) return value;
            const ref = `b${counter++}`;
            const { contentType, encoding, data } = describeLeaf(value);
            blobs.push({ ref, contentType, encoding, data, bytes: utf8Bytes(value) });
            return { [BLOB_MARKER]: ref };
        }
        if (Array.isArray(value)) return value.map(walk);
        if (value && typeof value === "object") {
            const out = {};
            for (const [k, v] of Object.entries(value)) out[k] = walk(v);
            return out;
        }
        return value;
    };
    return { payload: walk(payload), blobs };
}

// Replace each placeholder's synthetic ref with the server-minted blob id and
// stamp its size / content type onto the marker (so restore can render a
// download affordance without a second lookup). Returns
// { payload, blobRefs } where blobRefs is the flat array the sync body carries.
// `refToBlob` maps ref → { blobId, bytes, contentType }.
export function applyBlobIds(payload, refToBlob) {
    const blobRefs = [];
    const seen = new Set();
    const walk = (value) => {
        if (Array.isArray(value)) return value.map(walk);
        if (value && typeof value === "object") {
            if (Object.prototype.hasOwnProperty.call(value, BLOB_MARKER)) {
                const ref = value[BLOB_MARKER];
                const meta = refToBlob[ref];
                if (!meta || !meta.blobId) {
                    // Upload failed / was skipped for this leaf — drop the
                    // placeholder to a small marker so the turn still syncs;
                    // the content stays available locally in IDB.
                    return { [BLOB_MARKER]: null, unresolved: true };
                }
                if (!seen.has(meta.blobId)) {
                    seen.add(meta.blobId);
                    blobRefs.push({
                        blob_id: meta.blobId,
                        bytes: meta.bytes ?? 0,
                        content_type: meta.contentType || "application/octet-stream",
                    });
                }
                return {
                    [BLOB_MARKER]: meta.blobId,
                    bytes: meta.bytes ?? 0,
                    contentType: meta.contentType || "application/octet-stream",
                };
            }
            const out = {};
            for (const [k, v] of Object.entries(value)) out[k] = walk(v);
            return out;
        }
        return value;
    };
    return { payload: walk(payload), blobRefs };
}

// Restore-side: walk a payload and replace every resolved blob placeholder by
// awaiting `loadBlob(blobId, meta)` (which streams the bytes back). Unresolved
// placeholders (upload had failed) render as a short notice. Async because the
// loader hits the network; callers run it lazily on card expand.
//
// `onHydrated({ blobId, bytes, contentType, content })` reports each placeholder
// that loaded. Hydration is the one moment this device holds a blob's content
// AND knows the id it is already stored under — the id is otherwise discarded
// with the placeholder, and re-deriving it is impossible. The sync engine needs
// both to recognize the leaf as already-uploaded (see recordHydratedBlobs).
export async function hydrateBlobPlaceholders(payload, loadBlob, { onHydrated } = {}) {
    const walk = async (value) => {
        if (Array.isArray(value)) return Promise.all(value.map(walk));
        if (value && typeof value === "object") {
            if (Object.prototype.hasOwnProperty.call(value, BLOB_MARKER)) {
                const blobId = value[BLOB_MARKER];
                if (!blobId) return "[content unavailable — was not persisted]";
                try {
                    const content = await loadBlob(blobId, { bytes: value.bytes, contentType: value.contentType });
                    onHydrated?.({ blobId, bytes: value.bytes, contentType: value.contentType, content });
                    return content;
                } catch {
                    return "[content unavailable — failed to load]";
                }
            }
            const out = {};
            for (const [k, v] of Object.entries(value)) out[k] = await walk(v);
            return out;
        }
        return value;
    };
    return walk(payload);
}

// True if a payload still carries any (resolved) blob placeholder — lets the
// UI show a "load full content" affordance without hydrating eagerly.
export function hasBlobPlaceholders(payload) {
    let found = false;
    const walk = (value) => {
        if (found) return;
        if (Array.isArray(value)) { value.forEach(walk); return; }
        if (value && typeof value === "object") {
            if (Object.prototype.hasOwnProperty.call(value, BLOB_MARKER) && value[BLOB_MARKER]) {
                found = true;
                return;
            }
            Object.values(value).forEach(walk);
        }
    };
    walk(payload);
    return found;
}
